import { SvgSizingParams } from '@fortawesome-internal-tools/viggo'
import { all } from 'app/releases'

const CURRENT_YEAR = new Date().getFullYear()
const DEFAULT_SECONDARY_OPACITY = 0.4
const DEFAULT_SVG_SIZE = 16
const ALL_VERSIONS = all.together

export const transformIconColoring = function (styling, defaultStyles, colorPrimary, colorSecondary, opacityPrimary, opacitySecondary) {
  const styleArray = []
  let stringStyle = ''

  if (
    styling.colorPrimary === defaultStyles.colorPrimary &&
    styling.colorSecondary === defaultStyles.colorSecondary &&
    styling.opacityPrimary === defaultStyles.opacityPrimary &&
    styling.opacitySecondary === defaultStyles.opacitySecondary
  ) {
    return ''
  }

  if (styling?.colorPrimary && !styling?.opacityPrimary) {
    styleArray.push(colorPrimary)
  }

  if (styling?.colorSecondary && !styling?.opacitySecondary) {
    styleArray.push(colorSecondary)
  }

  if (styling?.opacityPrimary) {
    styleArray.push(colorPrimary)
    styleArray.push(opacityPrimary)
  }

  if (styling?.opacitySecondary) {
    styleArray.push(colorSecondary)
    styleArray.push(opacitySecondary)
  }

  styleArray.forEach((element) => {
    stringStyle += element
  })

  return stringStyle
}

export const handleSize = function (size) {
  switch (size) {
    case '2xs':
      return 10
    case 'xs':
      return 12
    case 'sm':
      return 14
    case 'lg':
      return 20
    case 'xl':
      return 24
    case '2xl':
      return 32
    default:
      return DEFAULT_SVG_SIZE
  }
}

export const svgStylingDifference = function (styling, defaultStyles) {
  let stylingChanges = {}

  // if duotone svg, and colorSecondary is equal to default, and colorPrimary has changed, then colorSecondary should be set to colorPrimary
  if (
    (styling?.colorSecondary === defaultStyles?.colorSecondary && styling?.colorPrimary !== defaultStyles?.colorPrimary) ||
    (styling?.colorPrimary !== defaultStyles?.colorPrimary && styling?.colorSecondary === defaultStyles?.colorSecondary && !stylingChanges?.colorSecondary)
  ) {
    stylingChanges = {
      colorPrimary: defaultStyles.colorPrimary === styling.colorPrimary ? null : styling.colorPrimary,
      colorSecondary: styling.colorPrimary,
      opacityPrimary: defaultStyles.opacityPrimary === styling.opacityPrimary ? null : styling.opacityPrimary,
      opacitySecondary: defaultStyles.opacitySecondary === styling.opacitySecondary ? null : styling.opacitySecondary,
      size: handleSize(styling.size)
    }
  } else {
    stylingChanges = {
      colorPrimary: defaultStyles?.colorPrimary === styling?.colorPrimary ? null : styling.colorPrimary,
      colorSecondary: defaultStyles?.colorSecondary === styling?.colorSecondary ? null : styling.colorSecondary,
      opacityPrimary: defaultStyles?.opacityPrimary === styling?.opacityPrimary ? null : styling.opacityPrimary,
      opacitySecondary: defaultStyles?.opacitySecondary === styling?.opacitySecondary ? null : styling.opacitySecondary,
      size: handleSize(styling.size)
    }
  }

  return stylingChanges
}

export const svgCodeSnippet = function ({ viggo, iconAbstract, svgStylingDifference, iconLicenseType, version }) {
  const { SvgIcon, SvgOutputOptions, SvgStylesheetMode } = viggo

  const iconHeightToWidthRatio = iconAbstract.height / iconAbstract.width

  const params = {
    roundToDecimalPlaces: 1,
    attributionCommentBody: `Font Awesome ${iconLicenseType} ${version} by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license${
      iconLicenseType === 'Free' || iconLicenseType === 'Brand' ? '/free' : ' (Commercial License)'
    } Copyright ${CURRENT_YEAR} Fonticons, Inc.`,
    pathFills: [svgStylingDifference.colorPrimary, svgStylingDifference.colorSecondary]
  }

  if (svgStylingDifference.size !== DEFAULT_SVG_SIZE) {
    params.height = svgStylingDifference.size.toString()
    params.width = (svgStylingDifference.size / iconHeightToWidthRatio).toString()
  }

  if (svgStylingDifference.opacityPrimary || svgStylingDifference.opacitySecondary) {
    const opacitySecondary = (svgStylingDifference.opacitySecondary || DEFAULT_SECONDARY_OPACITY).toString()
    const opacityPrimary = svgStylingDifference.opacityPrimary ? svgStylingDifference.opacityPrimary.toString() : undefined
    params.duotoneOpacities = [opacityPrimary, opacitySecondary]
  }

  const outputOptions = new SvgOutputOptions(params)
  outputOptions.configAsPresentationAttributesFile()

  const svg = SvgIcon.fromParts(iconAbstract.width, iconAbstract.height, iconAbstract.primaryPath, iconAbstract.secondaryPath, iconAbstract.isDuotone)

  return svg.toStringWithOptions(outputOptions, SvgStylesheetMode.Append)
}

export const fetchSvg = async function ({ viggo, isCustomIcon, icon, activeVersion, styling, isFullSvg = false }) {
  if (isCustomIcon) {
    if (isFullSvg) {
      // Only supported for Font Awesome 7 and above
      const { SvgIcon } = viggo
      const iconSVG = SvgIcon.fromParts(icon.width, icon.height, icon.path, icon.pathSecondary, !!icon.pathSecondary)

      iconSVG.formatForFullCanvas(SvgSizingParams.fa7Default())

      return {
        icon: icon,
        width: iconSVG?.width(),
        height: iconSVG?.height(),
        isDuotone: iconSVG?.isMultitone(),
        primaryPath: iconSVG?.primaryPath(),
        secondaryPath: iconSVG?.secondaryPath()
      }
    } else {
      // Handles cropped and older versions
      return {
        icon: icon,
        width: icon?.width,
        height: icon?.height,
        isDuotone: !!icon?.pathSecondary,
        primaryPath: icon?.path,
        secondaryPath: icon?.pathSecondary
      }
    }
  } else {
    try {
      const response = await fetch(getResourceUrl(activeVersion, icon, styling, isFullSvg))

      if (response.status >= 400) {
        throw new Error('Bad response from server')
      }

      const iconSVG = viggo.parseStandard(await response.text())

      if (!iconSVG) {
        throw new Error('Error parsing SVG')
      }

      return {
        icon: icon,
        width: iconSVG?.width(),
        height: iconSVG?.height(),
        isDuotone: iconSVG?.isMultitone(),
        primaryPath: iconSVG?.primaryPath(),
        secondaryPath: iconSVG?.secondaryPath()
      }
    } catch (error) {
      console.error(error)
      throw new Error('Failed fetching or parsing svg')
    }
  }
}

export const getResourceUrl = function (activeVersion, icon, styling, isFullSvg) {
  const versionFound = !!ALL_VERSIONS.find((obj) => obj.version === activeVersion)

  try {
    if (!versionFound || !icon?.name || typeof styling == 'undefined') {
      throw new Error('Error: getResourceUrl()')
    }

    const svgPath = isFullSvg ? 'svgs-full' : 'svgs'

    switch (styling?.family) {
      case 'classic':
        return `https://${FONTAWESOME.SITE_ASSETS_HOST}/releases/v${activeVersion}/${svgPath}/${styling?.style}/${icon?.name}.svg`
      case 'brands':
        return `https://${FONTAWESOME.SITE_ASSETS_HOST}/releases/v${activeVersion}/${svgPath}/brands/${icon?.name}.svg`
      case 'duotone':
        return `https://${FONTAWESOME.SITE_ASSETS_HOST}/releases/v${activeVersion}/${svgPath}/${styling?.family}${
          styling?.style === 'solid' ? '' : `-${styling.style}`
        }/${icon?.name}.svg`
      default:
        return `https://${FONTAWESOME.SITE_ASSETS_HOST}/releases/v${activeVersion}/${svgPath}/${styling?.family}-${styling?.style}/${icon?.name}.svg`
    }
  } catch (error) {
    console.error(error.message)
  }
}

export const downloadSVG = async function ({ viggo, styling, defaultStyles, icon, activeVersion, isCustomIcon, isFullSvg }) {
  const licenseType = icon.is_free ? 'Free' : 'Pro'
  const svg = await fetchSvg({ viggo, isCustomIcon, icon, activeVersion, styling, isFullSvg })
  const colors = svgStylingDifference(styling, defaultStyles)
  const svgFinalized = svgCodeSnippet({ viggo, iconAbstract: svg, svgStylingDifference: colors, iconLicenseType: licenseType, version: activeVersion })

  createDownload(svgFinalized, icon, styling, isCustomIcon, isFullSvg)
}

export const createDownload = function (svg, icon, styling, isCustomIcon, isFullSvg) {
  const blobUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
  const link = window.document.createElement('a')
  const name = [icon.name]

  if (!isCustomIcon) {
    if (styling.family === 'classic') {
      name.push(styling.style)
    } else {
      name.push(styling.family)
      name.push(styling.style)
    }
  }

  link.href = blobUrl
  link.download = `${name.join('-')}${isFullSvg ? '-full' : ''}.svg`
  link.click()

  URL.revokeObjectURL(blobUrl)
}
